import { memo } from 'react';
import { HeartOutlineIcon } from '@/assets/icons';
import { sanitizeUserInput, formatUserName } from '@/lib';
import { UserAvatar } from '@/components';
import type { PublicWishlistWithUser } from '../../model';

interface WishlistOwnerInfoProps {
  user: NonNullable<PublicWishlistWithUser['user']>;
  isFavorite: boolean;
  favoriteCount: number;
}

/**
 * Информация о владельце вишлиста со счётчиком избранного
 * 
 * Отображает:
 * - Аватар владельца (через общий UserAvatar)
 * - Имя владельца
 * - Иконку сердечка со счётчиком (только отображение, без интерактивности)
 */
export const WishlistOwnerInfo = memo(function WishlistOwnerInfo({ 
  user, 
  isFavorite, 
  favoriteCount 
}: WishlistOwnerInfoProps): JSX.Element {
  // Sanitize UGC для защиты от XSS
  const safeDisplayName = sanitizeUserInput(user.displayName);
  const safeLastName = sanitizeUserInput(user.lastName);

  // Сокращённый формат: "Имя Ф." — для компактного блока владельца
  const ownerName = formatUserName(safeDisplayName, safeLastName, { short: true }) || safeDisplayName;

  return (
    <div className="flex items-center gap-1 mt-1">
      {/* Аватар через общий компонент */}
      <UserAvatar
        userId={user.id}
        avatarUrl={user.avatarUrl}
        displayName={user.displayName}
        lastName={user.lastName}
        username={user.username}
        size="small"
        className="!size-5"
      />
      
      {/* Имя пользователя */}
      <span className="text-xs text-muted-foreground truncate flex-1">
        {ownerName}
      </span>

      {/* Счётчик избранного (только отображение) */}
      {(favoriteCount > 0 || isFavorite) && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <HeartOutlineIcon 
            size={14}
            className="text-muted-foreground"
          />
          <span className="text-xs font-normal text-muted-foreground">
            {favoriteCount}
          </span>
        </div>
      )}
    </div>
  );
});